import { CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import { EditableHeader } from '@/components/form/EditableHeader/EditableHeader';
import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout/SplitScreenLayout';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Footer } from '@/components/navigation/Footer';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { TextEditor } from '@/components/TextEditor/TextEditor';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { DocumentViewerWithSearch } from '@/modules/documents/components/DocumentViewerWithSearch';
import { canonicalizeSummaryForDirtyCheck } from '@/modules/documents/documents.utils';
import { ConfirmDiscardChangesModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.components';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { useDeleteMemoMutation } from '@/modules/memos/graphql/DeleteMemo.generated';
import { useSaveMemo } from '@/modules/memos/hooks/useSaveMemo';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { COLORS } from '@/styles/tokens/colors';

import { useEditMemoTrowserData } from './useEditMemoTrowserData';

interface MemoFormShape {
  memoTitle: string;
  memoContent: string;
}

interface EditMemoTrowserProps {
  householdId: string;
  memoId: string;
  onClose: () => void;
  onDeleted: () => void;
}

function normalizeMemoTitle(value: string) {
  return value.replace(/&nbsp;/g, ' ').trim();
}

export function EditMemoTrowser({
  householdId,
  memoId,
  onClose,
  onDeleted,
}: EditMemoTrowserProps) {
  const canEditMemo = useCanEditHouseholdData();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { saveMemo, loading: savingMemo } = useSaveMemo();
  const [deleteMemo, { loading: deletingMemo }] = useDeleteMemoMutation();
  const {
    documentId: viewerDocumentId,
    setOverrideDocumentId,
    setAnnotations,
    lastCitationParagraphIdsRef,
  } = useAnnotatedPDFViewerContext();

  const [memoIsDirty, setMemoIsDirty] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Register the memo's own dirty signal so a browser Back that closes this
  // trowser prompts for unsaved changes (see UnloadPromptProvider). The RHF
  // wrapper's auto-registration keys off `isTouched`, which the custom
  // title/content editors never set, so we report `memoIsDirty` explicitly.
  useOnBeforeUnloadPrompt(memoIsDirty);

  const formMethods = useForm<MemoFormShape>({
    defaultValues: {
      memoTitle: '',
      memoContent: '',
    },
  });
  const { getValues, handleSubmit, setValue, reset, watch, formState } =
    formMethods;

  const {
    memo,
    activeDocumentId,
    associatedDocuments,
    source,
    documentFilePages,
    loading,
  } = useEditMemoTrowserData({
    householdId,
    memoId,
    selectedDocumentId: viewerDocumentId ?? null,
    onNotFound: onClose,
  });

  const memoTitle = watch('memoTitle');

  const titleBaselineRef = useRef('');
  const contentInitialBaselineRef = useRef('');
  const contentEditorBaselineRef = useRef<string | null>(null);

  const computeMemoIsDirty = useCallback((title: string, content: string) => {
    const isTitleDirty = normalizeMemoTitle(title) !== titleBaselineRef.current;
    const contentBaseline =
      contentEditorBaselineRef.current ?? contentInitialBaselineRef.current;
    const isContentDirty =
      canonicalizeSummaryForDirtyCheck(content) !== contentBaseline;

    return isTitleDirty || isContentDirty;
  }, []);

  useEffect(() => {
    if (!memo) {
      return;
    }

    if (memoIsDirty) {
      return;
    }

    const nextTitle = memo.name;
    const nextContent = memo.content ?? '';
    titleBaselineRef.current = normalizeMemoTitle(nextTitle);
    contentInitialBaselineRef.current =
      canonicalizeSummaryForDirtyCheck(nextContent);
    contentEditorBaselineRef.current = null;

    reset({
      memoTitle: nextTitle,
      memoContent: nextContent,
    });
    setMemoIsDirty(false);
  }, [memo, memoIsDirty, reset]);

  // Clear the shared PDF-viewer state when the trowser unmounts. Closing via
  // browser Back strips the URL param and unmounts the trowser without calling
  // `handleClose`, so cleaning up on unmount (rather than only in the close
  // handler) keeps a stale document override, annotations, or citations from
  // leaking onto the details page.
  useEffect(() => {
    return () => {
      setOverrideDocumentId(null);
      setAnnotations(undefined);
      lastCitationParagraphIdsRef.current = [];
    };
    // Run only on unmount; the viewer setters and ref are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleSelectDocument = useCallback(
    (documentId: string) => {
      setOverrideDocumentId(documentId);
      setAnnotations(undefined);
    },
    [setAnnotations, setOverrideDocumentId]
  );

  const handleChangeTitle = useCallback(
    (newTitle: string) => {
      const nextTitle = normalizeMemoTitle(newTitle);
      const currentContent = getValues('memoContent');
      const isDirty = computeMemoIsDirty(nextTitle, currentContent);

      setMemoIsDirty(isDirty);
      setValue('memoTitle', nextTitle, {
        shouldDirty: isDirty,
      });
    },
    [computeMemoIsDirty, getValues, setValue]
  );

  const handleRequestDiscardAndClose = useCallback(() => {
    if (memoIsDirty) {
      setShowConfirmationModal(true);
      return;
    }

    handleClose();
  }, [handleClose, memoIsDirty]);

  const handleCloseConfirmDiscardModal = useCallback(
    (reason: 'confirm' | 'cancel') => {
      setShowConfirmationModal(false);
      if (reason === 'confirm') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleDeleteMemo = useCallback(async () => {
    if (!memo) {
      return;
    }

    try {
      await deleteMemo({
        variables: {
          id: memo.id,
        },
        refetchQueries: 'active',
        awaitRefetchQueries: true,
      });
      showFeedback('Memo deleted successfully', {
        variant: 'success',
      });
      onDeleted();
    } catch (error) {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(
        'Failed to delete memo',
        error instanceof Error ? error : new Error('Unknown delete error'),
        { memoId: memo.id }
      );
    }
  }, [deleteMemo, memo, onDeleted, reportError, showFeedback]);

  const onSubmit = handleSubmit(async (values) => {
    if (!memo || !canEditMemo) {
      return;
    }

    const memoName = normalizeMemoTitle(values.memoTitle);
    const memoContent = values.memoContent;

    if (!memoName) {
      showFeedback('Memo title is required');
      return;
    }

    if (!memoContent.trim()) {
      showFeedback('Memo content cannot be empty');
      return;
    }

    const didSave = await saveMemo({
      memoId: memo.id,
      memoName,
      memoContent,
    });

    if (didSave) {
      handleClose();
    }
  });

  const leftContent = useMemo(() => {
    if (!activeDocumentId) {
      return (
        <Stack p={2} justifyContent="center" height="100%">
          <Typography variant="body-xs">
            No source documents are available for this memo.
          </Typography>
        </Stack>
      );
    }

    return (
      <DocumentViewerWithSearch
        key={activeDocumentId}
        loading={loading}
        documentId={activeDocumentId}
        source={source}
        sx={{
          borderRadius: 0,
          border: 'none',
          backgroundColor: COLORS.PRIMITIVES.WHITE,
          boxShadow: 'none',
          px: 0,
        }}
        documentChromeProps={{
          shadowSx: {
            boxShadow: 'none',
          },
        }}
        pageWidthOffset={0}
        filePages={documentFilePages}
        associatedDocuments={associatedDocuments}
        onSelectDocument={handleSelectDocument}
      />
    );
  }, [
    activeDocumentId,
    associatedDocuments,
    documentFilePages,
    source,
    handleSelectDocument,
    loading,
  ]);

  const rightContent = useMemo(() => {
    if (loading && !memo) {
      return (
        <Stack
          p={2}
          height="100%"
          alignItems="center"
          justifyContent="center"
          spacing={1}
        >
          <CircularProgress size={24} />
          <Typography variant="body-xs">Loading memo editor...</Typography>
        </Stack>
      );
    }

    if (!memo) {
      return (
        <Stack p={2} justifyContent="center" height="100%">
          <Typography variant="title-s">Memo not found</Typography>
          <Typography variant="body-xs">
            This memo may have been deleted or is unavailable.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack
        flex={1}
        p={2}
        gap={2}
        height="100%"
        overflow={canEditMemo ? 'hidden' : 'auto'}
      >
        <EditableHeader
          value={memoTitle}
          onChange={handleChangeTitle}
          testId="EditMemoTrowser-titleInput"
          disabled={!canEditMemo}
        />
        <TextEditor
          key={memo.id}
          initialValue={memo.content ?? ''}
          initialValueKind="markdown"
          readOnly={!canEditMemo}
          hideBorder
          defaultAutofocusSelection="rootStart"
          innerSx={!canEditMemo ? { overflow: 'initial' } : {}}
          onUpdate={(content) => {
            if (contentEditorBaselineRef.current === null) {
              contentEditorBaselineRef.current =
                canonicalizeSummaryForDirtyCheck(content);
            }

            const currentTitle = getValues('memoTitle');
            const isDirty = computeMemoIsDirty(currentTitle, content);
            setMemoIsDirty(isDirty);
            setValue('memoContent', content, {
              shouldDirty: isDirty,
            });
          }}
        />
      </Stack>
    );
  }, [
    canEditMemo,
    computeMemoIsDirty,
    getValues,
    handleChangeTitle,
    loading,
    memo,
    memoTitle,
    setValue,
  ]);

  return (
    <>
      <FullScreenModal
        trowserKey={TrowserKey.EditMemo}
        isOpen
        onClose={handleRequestDiscardAndClose}
      >
        <form onSubmit={onSubmit} style={{ height: '100%' }}>
          <FullScreenModalHouseholdChatHost>
            {({ householdChatPane }) => (
              <SplitScreenLayout
                resizable
                leftContent={leftContent}
                rightContent={rightContent}
                rightPane={householdChatPane}
                footer={
                  <Footer
                    leftAction={
                      canEditMemo && memo ? (
                        <DeleteButton
                          size="sm"
                          variant="destructive-secondary"
                          loading={deletingMemo}
                          onConfirmDelete={() => {
                            void handleDeleteMemo();
                          }}
                          promptDeleteText="Delete memo"
                        />
                      ) : undefined
                    }
                    rightAction={
                      <Stack
                        spacing={1}
                        alignItems="center"
                        justifyContent="flex-end"
                        direction="row"
                      >
                        <Button
                          onClick={handleRequestDiscardAndClose}
                          variant="secondary"
                          size="sm"
                        >
                          Cancel
                        </Button>
                        {canEditMemo && (
                          <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            loading={formState.isSubmitting || savingMemo}
                            disabled={!memoIsDirty || deletingMemo}
                          >
                            Save changes
                          </Button>
                        )}
                      </Stack>
                    }
                  />
                }
              />
            )}
          </FullScreenModalHouseholdChatHost>
        </form>
      </FullScreenModal>
      <ConfirmDiscardChangesModal
        isOpen={showConfirmationModal}
        onClose={handleCloseConfirmDiscardModal}
      />
    </>
  );
}
